/**
 * DERIVED FIELD SYSTEM
 *
 * Allows form fields to automatically derive their values from other fields using
 * a function-based derivation config. Once a user manually edits a derived field,
 * it becomes independent and stops syncing.
 *
 * USAGE:
 * Pass a `formDerivations` map as the second argument to `useRanaFormDerivedState`.
 * Use a nested object matching your form's shape; place a function at any field
 * you want derived. The function receives the full form data and returns the derived value.
 *
 * EXAMPLES:
 *
 * 1. Derive `code` from `name`:
 *    useRanaFormDerivedState(
 *      { name: "", code: "" },
 *      { code: (data) => data.name.toLowerCase().replace(/\s+/g, "-") }
 *    )
 *    → code automatically becomes the slugified version of name
 *
 * 2. Nested derivation:
 *    useRanaFormDerivedState(
 *      { inputs: { studyArea: { id: "" } }, outputs: { name: "" } },
 *      {
 *        outputs: {
 *          name: (data) => `${data.inputs.studyArea.id}-derived`,
 *        },
 *      }
 *    )
 *    → outputs.name derives from inputs.studyArea.id
 *
 * BEHAVIOR:
 * - Derived fields sync automatically whenever any source field changes.
 * - When a user explicitly edits a derived field, derivation stops for that field.
 *
 */

import { GenericObjectType } from "@rjsf/utils";
import { isEqual } from "lodash";
import { useCallback, useRef, useState } from "react";

import { applyDerivedRules } from "./helpers/apply-derived-rules";
import {
  DerivedRule,
  FormDerivationRulesFor,
  flattenDerivedRules,
} from "./helpers/flatten-derived-rules";

export const useRanaFormDerivedState = <FormData extends GenericObjectType>(
  initialValues: FormData,
  formDerivations?: FormDerivationRulesFor<FormData>
) => {
  const initialRules: Record<string, DerivedRule<FormData>> = formDerivations
    ? flattenDerivedRules(formDerivations, initialValues)
    : {};

  const [rules, _setRules] = useState<Record<string, DerivedRule<FormData>>>(
    () => applyDerivedRules(initialValues, initialRules).updatedRules
  );
  // rulesRef keeps rules readable inside state updater closures without stale closure issues.
  // When setValues updaters are chained (e.g. React Strict Mode double-invoke, or two rapid
  // calls), the second updater would otherwise see stale rules from its useCallback closure
  // and fail to apply existing rules. Updating rulesRef synchronously inside the updater
  // ensures each chained call sees the rules that the previous call just set.
  const rulesRef = useRef<Record<string, DerivedRule<FormData>>>(initialRules);
  rulesRef.current = rules;

  const [values, _setValues] = useState<FormData>(
    () => applyDerivedRules(initialValues, initialRules).updatedValues
  );

  const setValues = useCallback(
    (updater: FormData | ((prev: FormData) => FormData)): void => {
      _setValues((prevValues) => {
        const newValues = typeof updater === "function" ? updater(prevValues) : updater;

        const { updatedValues, updatedRules } = applyDerivedRules(newValues, rulesRef.current);

        // Update rules if there are deactivations.
        // Also update rulesRef immediately so chained updater calls in the same batch
        // (e.g. from Strict Mode double-invoke) pick up the new rules without waiting
        // for a re-render to refresh the useCallback closure.
        if (!isEqual(rulesRef.current, updatedRules)) {
          rulesRef.current = updatedRules;
          _setRules(updatedRules);
        }

        return updatedValues;
      });
    },
    [] // stable: reads rules via rulesRef instead of closure
  );

  return [values, setValues] as const;
};
